import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Badge,
  Button,
  Flex,
  Icon,
  Spinner,
  Td,
  Text,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import React, { useRef, useState } from "react";
import {
  FaRedo,
  FaTrashAlt,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import { getToken } from "utils/auth";
import config from "config";

function InvitationsTableRow(props) {
  const { logo, expiration_time, email, is_registered, CreatedAt, setInvitations } = props;
  const textColor = useColorModeValue("gray.700", "white");
  const dialogBg = useColorModeValue("white", "navy.800");
  const toast = useToast();

  const [isRenewing, setIsRenewing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const cancelDeleteRef = useRef();

  const isExpired = !is_registered && new Date(expiration_time) < new Date();

  const showNotification = (title, description, status) => {
    toast({
      title: title,
      description: description,
      status: status,
      duration: 4000,
      isClosable: true,
      position: "top-right",
    });
  };

  const handleRenew = async () => {
    setIsRenewing(true);
    const token = getToken();
    try {
      await axios.post(
        `${config.API_URL}/invitation/send`,
        {
          email: email,
          url: config.URL,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const res = await axios.get(`${config.API_URL}/invitation/invitations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvitations(res.data);
      showNotification(
        "Invitation Renewed",
        `A new 72-hour invitation has been sent to ${email}.`,
        "success"
      );
    } catch (error) {
      console.error("Error renewing invitation:", error);
      showNotification(
        "Renewal Failed",
        error.response?.data?.error || "Error resending invitation.",
        "error"
      );
    } finally {
      setIsRenewing(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    const token = getToken();
    try {
      await axios.delete(`${config.API_URL}/invitation/` + email, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await axios.get(`${config.API_URL}/invitation/invitations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvitations(res.data);
      showNotification(
        "Invitation Deleted",
        `Invitation for ${email} has been revoked and removed.`,
        "success"
      );
      setIsDeleteOpen(false);
    } catch (error) {
      console.error("Error deleting invitation:", error);
      showNotification("Error", "Failed to delete invitation.", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <>
      <Tr>
        {/* Email & Avatar */}
        <Td minWidth={{ sm: "240px" }} pl="0px">
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Avatar
              name={email}
              src={logo}
              w="42px"
              h="42px"
              borderRadius="12px"
              me="14px"
              bg="teal.500"
              color="white"
            />
            <Flex direction="column">
              <Text fontSize="sm" color={textColor} fontWeight="bold">
                {email}
              </Text>
            </Flex>
          </Flex>
        </Td>

        {/* Creation Date */}
        <Td>
          <Text fontSize="xs" color="gray.500" fontWeight="medium">
            {formatDate(CreatedAt)}
          </Text>
        </Td>

        {/* Expiration Date */}
        <Td>
          <Text
            fontSize="xs"
            color={isExpired ? "red.400" : "gray.500"}
            fontWeight="medium"
          >
            {formatDate(expiration_time)}
          </Text>
        </Td>

        {/* Status */}
        <Td>
          {is_registered ? (
            <Badge
              colorScheme="green"
              variant="subtle"
              borderRadius="full"
              px="10px"
              py="4px"
              fontSize="xs"
              fontWeight="bold"
              display="inline-flex"
              alignItems="center"
              gap="4px"
            >
              <Icon as={FaCheckCircle} />
              Accepted
            </Badge>
          ) : isExpired ? (
            <Badge
              colorScheme="red"
              variant="subtle"
              borderRadius="full"
              px="10px"
              py="4px"
              fontSize="xs"
              fontWeight="bold"
              display="inline-flex"
              alignItems="center"
              gap="4px"
            >
              <Icon as={FaTimesCircle} />
              Expired
            </Badge>
          ) : (
            <Badge
              colorScheme="orange"
              variant="subtle"
              borderRadius="full"
              px="10px"
              py="4px"
              fontSize="xs"
              fontWeight="bold"
              display="inline-flex"
              alignItems="center"
              gap="4px"
            >
              <Icon as={FaClock} />
              Pending
            </Badge>
          )}
        </Td>

        {/* Delete Button */}
        <Td>
          <Button
            p="0px"
            bg="transparent"
            onClick={() => setIsDeleteOpen(true)}
            _hover={{ bg: "transparent" }}
          >
            <Flex
              color="red.500"
              cursor="pointer"
              align="center"
              p="8px 12px"
              borderRadius="8px"
              _hover={{ bg: "red.50", color: "red.600" }}
              transition="all 0.2s ease"
            >
              <Icon as={FaTrashAlt} me="6px" />
              <Text fontSize="xs" fontWeight="bold">
                REVOKE
              </Text>
            </Flex>
          </Button>
        </Td>

        {/* Renew Button */}
        <Td>
          <Button
            p="0px"
            bg="transparent"
            onClick={handleRenew}
            isLoading={isRenewing}
            _hover={{ bg: "transparent" }}
            isDisabled={is_registered}
          >
            <Flex
              color={is_registered ? "gray.400" : "teal.500"}
              cursor={is_registered ? "not-allowed" : "pointer"}
              align="center"
              p="8px 12px"
              borderRadius="8px"
              _hover={!is_registered ? { bg: "teal.50", color: "teal.600" } : {}}
              transition="all 0.2s ease"
            >
              <Icon as={FaRedo} me="6px" />
              <Text fontSize="xs" fontWeight="bold">
                RENEW
              </Text>
            </Flex>
          </Button>
        </Td>
      </Tr>

      {/* Revoke / Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => setIsDeleteOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={dialogBg} borderRadius="16px" mx="16px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              <Flex align="center" gap="10px">
                <Flex
                  w="36px"
                  h="36px"
                  borderRadius="10px"
                  bg="red.50"
                  color="red.500"
                  align="center"
                  justify="center"
                >
                  <Icon as={FaExclamationTriangle} w="18px" h="18px" />
                </Flex>
                <Text>Revoke Invitation</Text>
              </Flex>
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text fontSize="sm" color={textColor}>
                Are you sure you want to revoke the invitation for{" "}
                <strong>{email}</strong>?
              </Text>
              <Text fontSize="xs" color="gray.500" mt="8px">
                The registration link will immediately become invalid. You can issue a new invitation at any time.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter gap="10px">
              <Button
                ref={cancelDeleteRef}
                onClick={() => setIsDeleteOpen(false)}
                variant="ghost"
                borderRadius="10px"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmDelete}
                isLoading={isDeleting}
                loadingText="Revoking..."
                borderRadius="10px"
              >
                Revoke Invitation
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default InvitationsTableRow;
